import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ModalCustomComponent } from '@app/shared/modal-custom/modal-custom.component';
import { Geolocation } from '@awesome-cordova-plugins/geolocation/ngx';
import { BehaviorSubject, catchError, from, map, Observable, of, retry, switchMap, tap, throwError, timeout, timer } from 'rxjs';
import { LoaderService } from '../loader/loader.service';
import { Preferences } from '@capacitor/preferences';
declare var cordova: any;

export interface Coordinates {
   latitude: number;
   longitude: number;
   accuracy: number;
}

export enum GeolocationErrorType {
   PERMISSION_DENIED = 'PERMISSION_DENIED',
   POSITION_UNAVAILABLE = 'POSITION_UNAVAILABLE',
   TIMEOUT = 'TIMEOUT',
   UNKNOWN = 'UNKNOWN',
}

@Injectable({
   providedIn: 'root',
})
export class GpsV2Service {
   isNativePlatform: boolean = false;

   position = new BehaviorSubject<{
      latitude: number;
      longitude: number;
      accuracy: number;
   }>(undefined);

   modalDefineGPS: MatDialogRef<ModalCustomComponent>;

   // Nueva variable para almacenar las coordenadas
   private readonly gpsCoordinates = new BehaviorSubject<Coordinates>(
      undefined
   );

   // Variable para seguimiento de errores
   private readonly gpsError = new BehaviorSubject<GeolocationErrorType | null>(
      null
   );

   // Configuración
   private readonly MAX_RETRIES = 5;
   private readonly BASE_RETRY_DELAY = 1000; // ms
   private readonly GEOLOCATION_TIMEOUT = 15000; // 15 segundos

   constructor( private readonly geolocation: Geolocation, private readonly modal: MatDialog, private readonly loaderService: LoaderService
   ) {
      let platformType: 'ios' | 'android' | 'web' = 'web';
      platformType = typeof cordova !== 'undefined' ? (cordova.platformId as 'ios' | 'android') : 'web';
      this.isNativePlatform = platformType === 'ios' || platformType === 'android';
   }

   defineGPS(): Observable<boolean> {
      return new Observable<boolean>((res) => {
         this.loaderService.start();
         this.geolocation
            .getCurrentPosition({ enableHighAccuracy: true })
            .then((c) => {
               this.loaderService.stop();
               const { latitude, longitude, accuracy } = c.coords;
               this.isNativePlatform ? this.saveCoordinatesMobile(latitude, longitude, accuracy)  : this.saveCoordinatesToLocalStorage(latitude, longitude, accuracy);
               if (latitude && longitude && accuracy !== undefined) {
                  this.position.next({ latitude, longitude, accuracy });
                  res.next(true);
                  res.complete();
                  this.modalDefineGPS = undefined;
               } else {
                  this.loaderService.stop();
                  this.showLocationErrorModal();
                  res.next(false);
                  res.complete();
               }
            })
            .catch((error) => {
               this.showLocationErrorModal();
               res.next(false);
               res.complete();
            });
      });
   }

   /**
    * Verifica si la geolocalización está soportada en el navegador/dispositivo actual
    * @returns true si la geolocalización está soportada
    */
   isGeolocationSupported(): boolean {
      return 'geolocation' in navigator;
   }

   /**
    * Obtiene las coordenadas actuales con reintentos automáticos
    * @param options Opciones de configuración opcionales
    * @returns Observable con coordenadas (latitud, longitud, precisión)
    */
   getGpsCoordinates(
      options: {
         enableHighAccuracy?: boolean;
         timeout?: number;
         maximumRetries?: number;
      } = {}
   ): Observable<Coordinates> {
      // Resetear estado de error
      this.gpsError.next(null);

      // Si la geolocalización no está soportada, retornar error inmediatamente
      if (!this.isGeolocationSupported()) {
         return throwError(
            () =>
               new Error(
                  'La geolocalización no está soportada en este navegador/dispositivo'
               )
         );
      }

      this.loaderService.start();

      // Usar opciones proporcionadas o valores por defecto
      const geoOptions = {
         enableHighAccuracy: options.enableHighAccuracy ?? true,
         timeout: options.timeout || this.GEOLOCATION_TIMEOUT,
         maximumAge: 0, // Siempre obtener posición fresca
      };

      const maxRetries = options.maximumRetries || this.MAX_RETRIES;

      return from(this.geolocation.getCurrentPosition(geoOptions)).pipe(
         // Agregar timeout para evitar esperar indefinidamente
         timeout(geoOptions.timeout),

         // Mapear resultado de Geolocation a nuestra interfaz Coordinates
         map((position) => {
            const { latitude, longitude, accuracy } = position.coords;

            // Validar que tenemos todos los datos de coordenadas requeridos
            if (!latitude || !longitude || accuracy === undefined) {
               throw new Error('Datos de coordenadas inválidos');
            }

            return { latitude, longitude, accuracy };
         }),

         // Manejar datos recuperados exitosamente
         tap((coordinates) => {
            // Actualizar el BehaviorSubject de coordenadas
            this.gpsCoordinates.next(coordinates);
            // Detener el loader
            this.loaderService.stop();
            // También actualizar el BehaviorSubject position para compatibilidad
            this.position.next(coordinates);

            // Si había un modal de error abierto, cerrarlo
            if (this.modalDefineGPS) {
               this.modalDefineGPS.close();
               this.modalDefineGPS = undefined;
            }
         }),

         // Usar el operador retry moderno con la configuración de retraso
         retry({
            count: maxRetries,
            delay: (error, retryCount) => {
               console.log('Error de geolocalización:', error);

               // Categorizar error
               let errorType = GeolocationErrorType.UNKNOWN;

               if (error.code) {
                  switch (error.code) {
                     case 1: // PERMISSION_DENIED
                        errorType = GeolocationErrorType.PERMISSION_DENIED;
                        // No reintentar si el permiso fue denegado
                        return throwError(() => error);
                     case 2: // POSITION_UNAVAILABLE
                        errorType = GeolocationErrorType.POSITION_UNAVAILABLE;
                        break;
                     case 3: // TIMEOUT
                        errorType = GeolocationErrorType.TIMEOUT;
                        break;
                  }
               } else if (error.name === 'TimeoutError') {
                  errorType = GeolocationErrorType.TIMEOUT;
               }

               // Actualizar estado de error
               this.gpsError.next(errorType);

               // Calcular el retraso con backoff exponencial
               const delay = this.BASE_RETRY_DELAY * Math.pow(2, retryCount);
               console.log(
                  `Reintento ${
                     retryCount + 1
                  }/${maxRetries} después de ${delay}ms`
               );

               // Devolver un observable que emita después del retraso calculado
               return timer(delay);
            },
         }),

         // Manejar errores después de que todos los reintentos han fallado
         catchError((error) => {
            this.loaderService.stop();

            // Categorizar el error
            let errorType = GeolocationErrorType.UNKNOWN;
            let errorMessage = 'Error desconocido';

            if (error.code) {
               switch (error.code) {
                  case 1:
                     errorType = GeolocationErrorType.PERMISSION_DENIED;
                     errorMessage = 'Permiso de ubicación denegado';
                     break;
                  case 2:
                     errorType = GeolocationErrorType.POSITION_UNAVAILABLE;
                     errorMessage = 'Posición no disponible';
                     break;
                  case 3:
                     errorType = GeolocationErrorType.TIMEOUT;
                     errorMessage = 'Solicitud de geolocalización expirada';
                     break;
               }
            } else if (error.name === 'TimeoutError') {
               errorType = GeolocationErrorType.TIMEOUT;
               errorMessage = 'Solicitud de geolocalización expirada';
            }

            // Actualizar estado de error
            this.gpsError.next(errorType);

            // Mostrar modal de error
            this.showLocationErrorModal();

            // Retornar un error detallado para mejor depuración
            return throwError(
               () =>
                  new Error(
                     `Fallo al obtener coordenadas después de ${maxRetries} intentos. Error: ${errorMessage}`
                  )
            );
         })
      );
   }

   /**
    * Obtiene el valor actual de las coordenadas
    * @returns Coordenadas actuales o undefined si no están disponibles
    */
   getCurrentGpsCoordinates(): Coordinates | undefined {
      return this.gpsCoordinates.getValue();
   }

   /**
    * Obtiene las coordenadas como un observable
    * @returns Observable de coordenadas
    */
   getGpsCoordinatesAsObservable(): Observable<Coordinates> {
      return this.gpsCoordinates.asObservable();
   }

   /**
    * Obtiene el error actual de geolocalización si existe
    * @returns Tipo de error actual o null si no hay error
    */
   getGpsError(): GeolocationErrorType | null {
      return this.gpsError.getValue();
   }

   /**
    * Obtiene el error de geolocalización como un observable
    * @returns Observable de tipos de error
    */
   getGpsErrorAsObservable(): Observable<GeolocationErrorType | null> {
      return this.gpsError.asObservable();
   }

   private showLocationErrorModal() {
      if (!this.modalDefineGPS) {
         this.modalDefineGPS = this.modal.open(ModalCustomComponent, {
            panelClass: 'mnf-modal-container',
            width: '95vw',
            maxWidth: '650px',
            autoFocus: false,
            maxHeight: '95vh',
         });

         this.modalDefineGPS.componentInstance.config = {
            header: 'Activa tu ubicación',
            img: 'https://static.monific.com/assets/assets-app/img/icon/img/geolocalization.svg',
            title: '¡Importante!',
            message: `<p class="fs-14">Requerimos tu ubicación para poder continuar. Por favor, activa la geolocalización en tu dispositivo.</p>`,
            buttonText: 'Entendido',
         };
      }
   }

   /** Guardar datos de ubicación el el sessionStorage o en Preference de la APP */
   saveCoordinatesInStorage(latitude: number, longitude: number, accuracy: number) {
      if (this.isNativePlatform) {
         this.saveCoordinatesMobile(latitude, longitude, accuracy);
      } else {
         this.saveCoordinatesToLocalStorage(latitude, longitude, accuracy);
      }
   }

   /** Guardar Latitud, Longitud y Accuracy en sessionStorage */
   saveCoordinatesToLocalStorage(latitude: number, longitude: number, accuracy: number) {
      let coordinates = `${latitude}|${longitude}|${accuracy}`;
      sessionStorage.setItem('GPS_LOCATION', JSON.stringify(coordinates));
   }

   /** Guardar Latitud, Longitud y Accuracy en mobile */
   async saveCoordinatesMobile(latitude: number, longitude: number, accuracy: number) {
      try {
         let coordinates = `${latitude}|${longitude}|${accuracy}`;         
         await Preferences.set({ key: 'GPS_LOCATION', value: JSON.stringify(coordinates) })
      } catch (error) {
         console.error('Error al guardar coordenadas en mobile:', error);
      }
   }

   /** Obtener ubicación almacenada en mobile para reutilizarla */
   async getStoredCoordinatesMobile(): Promise<{ GPS_LOCATION: string } | false> {
      try {
         const keys = [ 'GPS_LOCATION' ];
         const entries = await Promise.all(keys.map(key => Preferences.get({ key })));

         const [ GPS_LOCATION ] = entries.map(entry => entry.value);

         if( GPS_LOCATION ) {
            return { GPS_LOCATION };
         }
         return false;
      } catch (error) {
         console.error('Error al obtener coordenadas almacenadas:', error);
         return false;
      }
   }

   /** Obtener JSON del localStorage para obtener latitud, longtiud, Accuracy */
   async getCoordinatesFromLocalStorage(): Promise<Coordinates | null> {
      let coordinates: string | null = null;

      if (this.isNativePlatform) {
         const res: any = await this.getStoredCoordinatesMobile();
         coordinates = res?.GPS_LOCATION ?? null;
      } else {
         coordinates = sessionStorage.getItem('GPS_LOCATION');
      }

      if (coordinates) {
         try {
            const [latitude, longitude, accuracy] = JSON.parse(coordinates).split('|').map(Number);
            return { latitude, longitude, accuracy };
         } catch (e) {
            console.error('Error al parsear coordenadas:', e);
            return null;
         }
      }

      return null;
   }

   /** Retornar coordenadas separadas de ya: latitude - longitude - accuracy */
   useCoordinates(coords: any): Coordinates | null {
      if (coords) {
         const [latitude, longitude, accuracy] = coords.split('|').map(Number);
         return { latitude, longitude, accuracy };
      }
      return null;
   }

   /** Función principal para obtener la ubicación en los componentes y/o servicios */
   getOrRequestCoordinates(): Observable<Coordinates> {
      return from(this.getCoordinatesFromLocalStorage()).pipe(
         switchMap((storedCoords) => {
            if (storedCoords) {
               return of(storedCoords);
            }

            return this.defineGPS().pipe(
               switchMap((success) => {
                  if (!success) {
                     return throwError(() => new Error('Geolocalización no autorizada'));
                  }

                  return from(this.getCoordinatesFromLocalStorage()).pipe(
                     switchMap((latest) => {
                        if (!latest) {
                           return throwError(() => new Error('No se pudo obtener ubicación después de activación de GPS'));
                        }
                        return of(latest);
                     })
                  );
               })
            );
         })
      );
   }  

}
